import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { AppStreamEnvironment, ErmCapabilityApiClientService, GetScaleAccountProgress } from 'erm-sdk';

@Injectable()
export class EnvironmentUiService {
  // Dependencies
  private readonly ermCapabilityApiClient = inject(ErmCapabilityApiClientService);

  // External loading signal (set by components)
  private sharedLoading?: WritableSignal<boolean>;

  // Source of truth
  private readonly _environment = signal<AppStreamEnvironment | null>(null);

  // Public API (readonly where possible)
  public readonly environment$: Signal<AppStreamEnvironment | null> = this._environment.asReadonly();
  public readonly hasEnvironment$: Signal<boolean> = computed(() => this._environment() !== null);
  public readonly scaleProgress$: WritableSignal<GetScaleAccountProgress | null> = signal<GetScaleAccountProgress | null>(null);

  public readonly isFunctional$: Signal<boolean> = computed(() => {
    const environment = this._environment();
    if (!environment) {
      return false;
    }
    return Boolean(environment.isAvailable && environment.isEnabled && !environment.inMaintenanceMode);
  });

  public readonly canLaunch$: Signal<boolean> = computed(() => {
    const environment = this._environment();
    if (!environment) {
      return false;
    }
    return this.isFunctional$() && environment.scalableServicesHealthy === true;
  });

  public setSharedLoading(loading:  WritableSignal<boolean>): void {
    this.sharedLoading = loading;
  }

  public setEnvironment(environment: AppStreamEnvironment | null): void {
    this._environment.set(environment ?? null);
  }

  public clearEnvironment(): void {
    this._environment.set(null);
  }

  public async reloadEnvironment(): Promise<void> {
    const id = this._environment()?.environmentId;
    if (!id) {
      return; // nothing to reload
    }

    try {
      const response = await this.ermCapabilityApiClient.getAppStreamEnvironment(id);
      this._environment.set(response.body?.appStreamEnvironment ?? null);
    }
    catch (error) {
      console.error(error);
      this._environment.set(null);
      throw error;
    }
    finally {
      this.sharedLoading?.set(false);
    }
  }
}
